import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { motion } from 'framer-motion';
import { 
  Hammer, 
  FlaskConical, 
  Gem, 
  Scroll, 
  Mountain, 
  ChefHat,
  Leaf,
  Shield,
  Sword,
  Wand2,
  BookOpen,
  Wrench,
  Settings,
  Package,
  ShoppingCart,
  LucideIcon
} from 'lucide-react';

interface Profession {
  id: string;
  name: string;
  icon: LucideIcon;
  color: string;
}

// General guides that appear at the top
const generalGuides: Profession[] = [
  { id: 'general-crafting', name: 'General Crafting Guide', icon: Settings, color: 'from-blue-500 to-indigo-600' },
  { id: 'general-gathering', name: 'General Gathering Guide', icon: Package, color: 'from-green-500 to-teal-600' },
  { id: 'consumable-buyers', name: 'Consumable Buyer\'s Guide', icon: ShoppingCart, color: 'from-purple-500 to-pink-600' },
  { id: 'gear-buyers', name: 'Gear Buyer\'s Guide', icon: Shield, color: 'from-orange-500 to-amber-600' },
];

const professions: Profession[] = [
  { id: 'alchemy', name: 'Alchemy', icon: FlaskConical, color: 'from-green-500 to-emerald-600' },
  { id: 'blacksmithing', name: 'Blacksmithing', icon: Hammer, color: 'from-gray-500 to-gray-700' },
  { id: 'enchanting', name: 'Enchanting', icon: Wand2, color: 'from-purple-500 to-indigo-600' },
  { id: 'engineering', name: 'Engineering', icon: Wrench, color: 'from-yellow-500 to-orange-600' },
  { id: 'herbalism', name: 'Herbalism', icon: Leaf, color: 'from-green-400 to-green-600' },
  { id: 'inscription', name: 'Inscription', icon: Scroll, color: 'from-amber-500 to-yellow-600' },
  { id: 'jewelcrafting', name: 'Jewelcrafting', icon: Gem, color: 'from-pink-500 to-rose-600' },
  { id: 'leatherworking', name: 'Leatherworking', icon: Shield, color: 'from-amber-600 to-orange-700' },
  { id: 'mining', name: 'Mining', icon: Mountain, color: 'from-stone-500 to-stone-700' },
  { id: 'skinning', name: 'Skinning', icon: Sword, color: 'from-red-500 to-red-700' },
  { id: 'tailoring', name: 'Tailoring', icon: BookOpen, color: 'from-blue-500 to-cyan-600' },
  { id: 'cooking', name: 'Cooking', icon: ChefHat, color: 'from-orange-500 to-red-600' },
];

const Professions: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
  }, [authLoading, user, navigate]);

  if (authLoading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-bold gradient-text">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="page-container">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">Profession Guides</span>
          </h1>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Comprehensive guides for all World of Warcraft professions. Click on a profession to view detailed guides and tips.
          </p>
        </motion.div>

        {/* General Guides Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">
            <span className="gradient-text">General Guides</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {generalGuides.map((guide, index) => {
              const IconComponent = guide.icon;
              return (
                <motion.div
                  key={guide.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.05 }}
                >
                  <Link
                    to={`/professions/${guide.id}`}
                    className="block group"
                  >
                    <div className="glass p-6 rounded-xl hover:bg-surface-light transition-all duration-300 transform hover:scale-105 h-full flex flex-col items-center justify-center text-center border-2 border-cyan-400/30 hover:border-cyan-400/60">
                      <div className={`w-16 h-16 bg-gradient-to-r ${guide.color} rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                        <IconComponent size={32} className="text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-text-primary group-hover:text-cyan-400 transition-colors">
                        {guide.name}
                      </h3>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Individual Professions Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">
            <span className="gradient-text">Individual Professions</span>
          </h2>
          {/* Professions Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {professions.map((profession, index) => {
            const IconComponent = profession.icon;
            return (
              <motion.div
                key={profession.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
              >
                <Link
                  to={`/professions/${profession.id}`}
                  className="block group"
                >
                  <div className="glass p-6 rounded-xl hover:bg-surface-light transition-all duration-300 transform hover:scale-105 h-full flex flex-col items-center justify-center text-center">
                    <div className={`w-16 h-16 bg-gradient-to-r ${profession.color} rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                      <IconComponent size={32} className="text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-text-primary group-hover:text-cyan-400 transition-colors">
                      {profession.name}
                    </h3>
                  </div>
                </Link>
              </motion.div>
            );
          })}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Professions;

